/**
 * Settings mode of the workspace (the prototype's `settingsList() + settingsEditor()`): the item column
 * and the editor of the category in the route, plus the settings top bar and the unsaved-changes guard
 * (in-app navigation via the router's blocker, tab close via `beforeunload`).
 *
 * Routes: /settings/:category[/:recordId] — recordId 'new' (optionally ?from=<id> to duplicate).
 * Desktop with no record in the URL shows the first item, as the prototype does; a phone shows the list.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useBlocker, useParams, useSearchParams } from 'react-router-dom';
import { openDialog } from '../../app/dialogs.js';
import { ui } from '../../app/ui-store.js';
import { findCategory, settingsCategories } from '../registry.js';
import { SettingsLocationContext, type SettingsLocation } from './context.js';
import { ItemList } from './ItemList.js';
import { SaveToolsSlot, SettingsToolbar } from './SettingsToolbar.js';

export function useBeforeUnloadWhenDirty(): void {
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!ui.get().draft?.dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}

/** Ask Keep editing / Discard / Save before any route change away from a dirty editor. */
function useUnsavedGuard(): void {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!ui.get().draft?.dirty) return false;
    return currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search;
  });
  const asked = useRef<unknown>(null);
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      asked.current = null;
      return;
    }
    if (asked.current === blocker.location) return;
    asked.current = blocker.location;
    const draft = ui.get().draft;
    openDialog('unsaved-changes', {
      onKeep: () => blocker.reset(),
      onDiscard: () => {
        draft?.discard();
        blocker.proceed();
      },
      onSave: async () => {
        if (draft && (await draft.save())) blocker.proceed();
        else blocker.reset();
      },
    });
  }, [blocker]);
  useBeforeUnloadWhenDirty();
}

export function SettingsLayout() {
  const params = useParams();
  const [search] = useSearchParams();
  const definition = findCategory(params.category);
  useUnsavedGuard();
  const [saveSlot, setSaveSlot] = useState<HTMLElement | null>(null);

  if (!definition) return <Navigate to={`/settings/${settingsCategories()[0]?.id ?? 'sources'}`} replace />;
  return <CategoryScreen key={definition.id} definitionId={definition.id} recordParam={params.recordId ?? null} duplicateOf={search.get('from')} saveSlot={saveSlot} setSaveSlot={setSaveSlot} />;
}

function CategoryScreen({ definitionId, recordParam, duplicateOf, saveSlot, setSaveSlot }: {
  definitionId: string;
  recordParam: string | null;
  duplicateOf: string | null;
  saveSlot: HTMLElement | null;
  setSaveSlot(element: HTMLElement | null): void;
}) {
  const definition = findCategory(definitionId);
  if (!definition) throw new Error(`Unknown settings category ${definitionId}`);
  const { items, isLoading } = definition.useItems();
  const recordId = recordParam ?? items[0]?.id ?? null;
  const current = items.find((i) => i.id === recordId) ?? null;
  const location = useMemo<SettingsLocation>(
    () => ({ category: definition.id, definition, recordId, duplicateOf: recordId === 'new' ? duplicateOf : null }),
    [definition, recordId, duplicateOf],
  );
  const Editor = definition.Editor;
  const List = definition.List ?? ItemList;
  return (
    <SettingsLocationContext.Provider value={location}>
      <SettingsToolbar category={definition} current={recordId === 'new' ? null : current} saveSlotRef={setSaveSlot} />
      <List category={definition} items={items} isLoading={isLoading} selectedId={recordId === 'new' ? null : recordId} />
      <SaveToolsSlot.Provider value={saveSlot}>
        <Editor key={`${recordId ?? ''}|${location.duplicateOf ?? ''}`} recordId={recordId} duplicateOf={location.duplicateOf} />
      </SaveToolsSlot.Provider>
    </SettingsLocationContext.Provider>
  );
}
